// 169 GLANCE 1 — THE TICKS AND THE LIST THEY MUST AGREE WITH.
//
// The pro-forma modal's source picker keys its ticks by INVOICE ID (`srcSel`),
// and the list it ticks against is REFETCHED whenever the document's currency
// changes — R6, same currency or nothing. A tick therefore outlives its row,
// and before this module the three readers of that map disagreed about which
// population they were over: the Σ (`sourcesTotal`) and the per-row ceiling
// (`srcCeiling`) reduced the WHOLE tick map, while the ladder's source names
// and the create payload read only the CURRENT list.
//
// Laurent's glance is the shape of that disagreement, measured on staging:
// case SMK-01 offers PF-2026/020 (EUR, unconsumed 994,01) under ?currency=EUR
// and PF-2026/025 (USD, unconsumed 4 840) under ?currency=USD. Tick the EUR
// retainer at its 994,01 ceiling, flip the document to USD, tick the one row
// now on offer: the stale 994,01 is still inside `others`, so PF-2026/025's
// ceiling computes to 0 and it ticks at 0 — while the Σ still reads 994,01.
// The ladder printed "Paid retainer deduction incl. VAT −USD 994,01" over the
// name "PF-2026/025", and the payload would have sent `[{PF-2026/025, 0}]`,
// so the SAVED document's deduction would have been 0. Two numbers, one line,
// neither of them the other's.
//
// The cure is one population for all three readers. `tickedIn` is the only way
// into the map, and every figure is derived from it, so a sum can never see a
// row the user cannot. The modal ALSO clears the ticks on every refetch (the
// other half of the fix); `tickedIn` is what makes a leftover HARMLESS if one
// ever survives — a guard against the class, not just the instance. Since 209
// §2 the two figures are the SERVER's (below); the modal sends the ticked rows
// of the list it shows and displays what the compute answers.

/** One row's tick: whether it is on, and the amount typed against it. */
export interface SourceTick {
  on: boolean;
  amount: number;
}

/**
 * The picker's whole state, keyed by invoice id. The value admits `undefined`
 * DELIBERATELY: an id with no tick is the ordinary case, and a `Record` whose
 * values are declared always-present would tell the reader (and the linter) a
 * lie that this module exists to stop believing.
 */
export type SourceTicks = Record<number, SourceTick | undefined>;

/** The only shape these functions read off a source row. */
export interface SourceIdentity {
  id: number;
}

/**
 * The ticked rows OF A GIVEN LIST — the one population every figure below is
 * taken over. A tick whose row is not in `rows` is not ticked, whatever the
 * map says.
 */
export function tickedIn<T extends SourceIdentity>(
  rows: readonly T[] | null | undefined,
  sel: SourceTicks,
): T[] {
  return (rows ?? []).filter((r) => sel[r.id]?.on ?? false);
}

// 209 §2 — THE Σ AND THE PER-ROW CEILING LEFT THIS FILE FOR THE SERVER
// (server/src/lib/invoiceSourceDraws.js `sourcesTotalIn` / `sourceCeilingIn`,
// their arithmetic byte for byte — recordings/209-source-ceiling replays the
// answers frozen before the move). The compute hands the modal both, over the
// same offer GET /sources lists, and the save asks the same question; what
// stays here is only what the display needs: the ticked rows of a list, the
// figures a compute served, a served document's draws, and the ticks an edit
// opens with.

/** What the compute answered for the drawer: the Σ, and each offered row's ceiling. */
export interface ServedSourceFigures {
  total: number;
  ceilings: Map<number, number>;
}

/**
 * THE DRAWER'S FIGURES AS THE COMPUTE SERVED THEM (`sourcesTotal`,
 * `sourceCeilings: [{ invoiceId, ceiling }]`). Takes `unknown` for `drawsIn`'s
 * reason — the seam where a served body becomes typed. An answer that carries
 * none (no ticks sent, or no answer yet) is a Σ of 0 and no ceiling.
 */
export function servedSourceFigures(served: unknown): ServedSourceFigures {
  const out: ServedSourceFigures = { total: 0, ceilings: new Map() };
  if (typeof served !== "object" || served === null) return out;
  const body = served as { sourcesTotal?: unknown; sourceCeilings?: unknown };
  const total = Number(body.sourcesTotal);
  if (Number.isFinite(total)) out.total = total;
  if (Array.isArray(body.sourceCeilings)) {
    for (const raw of body.sourceCeilings as unknown[]) {
      if (typeof raw !== "object" || raw === null) continue;
      const row = raw as { invoiceId?: unknown; ceiling?: unknown };
      const id = Number(row.invoiceId);
      const ceiling = Number(row.ceiling);
      if (Number.isInteger(id) && id > 0 && Number.isFinite(ceiling)) out.ceilings.set(id, ceiling);
    }
  }
  return out;
}

/**
 * ONE SOURCE'S EXISTING DRAW on a document, as the by-id read serves it
 * (`retainerSources`, 161 §5 — `id` joined the row in 171 §1e).
 */
export interface SourceDraw {
  id: number;
  amount: number;
}

/**
 * WHAT A SERVED DOCUMENT SAYS IT DRAWS, or `null` when the read did not say.
 *
 * The distinction is load-bearing, and it is 171 §1e's ruling: `null` means
 * "this document's draws are unknown" — a pre-161 row with a stored
 * `retainerDeduction` and no allocations behind it, or a read that does not
 * carry the field at all — and the edit arm must then keep its hands off both
 * the picker and the ledger. An EMPTY ARRAY means "it draws nothing", which is
 * an ordinary document anyone may tick a retainer onto.
 *
 * Takes `unknown` because it is the seam where a served body becomes typed:
 * every row is measured here rather than trusted.
 */
export function drawsIn(served: unknown): SourceDraw[] | null {
  if (!Array.isArray(served)) return null;
  const out: SourceDraw[] = [];
  for (const raw of served) {
    if (typeof raw !== "object" || raw === null) continue;
    const row = raw as { id?: unknown; amount?: unknown };
    const id = Number(row.id);
    const amount = Number(row.amount);
    if (Number.isInteger(id) && id > 0 && amount > 0) out.push({ id, amount });
  }
  return out;
}

/**
 * THE TICKS AN EDIT OPENS WITH: the document's existing draws, expressed
 * against THE LIST THEY WILL BE TICKED IN.
 *
 * Taken over `rows` for exactly the reason `tickedIn` is (169 GLANCE 1): a draw
 * whose source is not on the offer is not a tick, whatever the document says,
 * and a Σ must never see a row the user cannot. On an edit the offer is
 * measured with this document's own claims excluded (`?forInvoiceId=`), so a
 * source it has fully consumed IS on that list and does seed.
 */
export function seedTicks(
  rows: readonly SourceIdentity[] | null | undefined,
  draws: readonly SourceDraw[] | null | undefined,
): SourceTicks {
  if (!draws || draws.length === 0) return {};
  const byId = new Map<number, number>();
  for (const d of draws) byId.set(d.id, d.amount);
  const out: SourceTicks = {};
  for (const r of rows ?? []) {
    const amount = byId.get(r.id);
    if (amount !== undefined) out[r.id] = { on: true, amount };
  }
  return out;
}
